import * as Yup from "yup";
import { MITIGATE_COLORS } from "../../../../shared/sharedConstants";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  PointElement,
  LineElement,
  Title,
  Legend,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { viewClickedIncident } from "../../../../utils/helpers/incidentListUtils";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { forEach } from "lodash";

ChartJS.register(
  CategoryScale,
  LinearScale,
  Tooltip,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Legend,
  ChartDataLabels
);

export const woundAnalysisValidator = () => {
  return Yup.object({
    facilityInfo: Yup.object().shape({
      value: Yup.string().required("Facility is required"),
    }),
    startDate: Yup.date().required("Start date is required"),
    endDate: Yup.date().required("End date is required"),
  });
};

export const woundIncidentUnitPlaceCols: any[] = [
  {
    Header: "Unit",
    accessor: "unit",
  },
  {
    Header: "Total Incidents",
    accessor: "totalIncidents",
  },
  {
    Header: "Pressure Injuries",
    accessor: "pressureInjuries",
  },
  {
    Header: "Skin Tears",
    accessor: "skinTears",
  },
  {
    Header: "Bruises",
    accessor: "bruises",
  },
  {
    Header: "Rashes",
    accessor: "rashes",
  },
  {
    Header: "Other",
    accessor: "other",
  }
  
];

export const lineOptions = {
  responsive: true,
  interaction: {
    mode: "index" as const,
    intersect: false,
  },
  stacked: false,
  plugins: {
    tooltip: {
      enabled: true,
      intersect: false,
    },
    legend: {
      position: "top" as const,
    },
    datalabels: {
      display: true,
      color: "black",
      formatter: (value: any) => handleFormatter(value),
      font: {
        size: 16,
      },
    },
  },
  scales: {
    y: {
      ticks: {
        display: false,
      },
    },
  },
  onHover: (e: any, chart: any) => {
    e.native.target.style.cursor = chart[0] ? "pointer" : "default";
  },
};

export const handleFormatter = (value: string) => {
  if (Number(value) > 0) return value;
  return null;
};

export const barOptions = (fn: Function) => {
  return {
    responsive: true,
    plugins: {
      legend: {
        display: false,
        position: "top" as const,
      },
      datalabels: {
        display: true,
        color: "black",
        formatter: (value: any) => handleFormatter(value),
        font: {
          size: 16,
        },
      },
    },
    scales: {
      y: {
        ticks: {
          display: false,
        },
      },
    },
    onClick: (e: any, chart: any) => {
      fn(chart);
    },
    onHover: (e: any, chart: any) => {
      e.native.target.style.cursor = chart[0] ? "pointer" : "default";
    },
  };
};

export const barOptionsHorizontal = (fn: Function) => {
  return {
    indexAxis: "y" as const,
    elements: {
      bar: {
        borderWidth: 2,
      },
    },
    responsive: true,
    plugins: {
      legend: {
        display: false,
        position: "right" as const,
      },
      datalabels: {
        display: true,
        color: "black",
        formatter: (value: any) => handleFormatter(value),
        font: {
          size: 16,
        },
      },
    },
    scales: {
      x: {
        ticks: {
          display: false,
        },
      },
    },
    onClick: (e: any, chart: any) => {
      fn(chart);
    },
    onHover: (e: any, chart: any) => {
      e.native.target.style.cursor = chart[0] ? "pointer" : "default";
    },
  };
};

export const handlePointClicked = (chart: any, labels: any) => {
  const index = chart[0].index;
  const label = labels[index];
  return label;
};

export function getMetrics(obj: any) {
  const result: any[] = [];

  for (const key in obj) {
    if (obj.hasOwnProperty(key) && !key.includes("total")) {
      const value = obj[key];
      if (typeof value !== "object" || value === null) {
        result.push({ title: convertToTitleCase(key), value });
      }
    }
  }

  return result;
}

export function convertToTitleCase(inputString: string) {
  return inputString
    .replace("ByMonth", "") // Remove "ByMonth"
    .replace(/([A-Z])/g, " $1") // Add spaces before uppercase letters
    .replace(/^./, (str) => str.toUpperCase()); // Capitalize the first letter
}

export function isValidObject(property: any) {
  return (
    property !== null &&
    typeof property === "object" &&
    !Array.isArray(property) &&
    Object.keys(property).length > 0
  );
}

function getUniqueSortedLabels(obj: any) {
  const uniqueLabels = new Set();
  Object.keys(obj).forEach((metric) => {
    if (isValidObject(obj[metric])) {
      Object.keys(obj[metric]).forEach((month) => {
        uniqueLabels.add(month);
      });
    }
  });
  const result = Array.from(uniqueLabels);
  return result;
}

export function convertObjectToChartData(obj: any) {
  const datasets: any[] = [];

  // Determine unique labels (months) from the input data
  const uniqueLabels = getUniqueSortedLabels(obj);
  const labels = sortLabelsByDate(uniqueLabels);

  // Initialize data arrays for each metric that has a valid month object
  const dataArrays: any = {};
  for (const metric in obj) {
    if (
      obj[metric] !== null &&
      typeof obj[metric] === "object" &&
      !Array.isArray(obj[metric]) &&
      Object.keys(obj[metric]).length > 0
    ) {
      dataArrays[metric] = Array(labels.length).fill(0);
    }
  }

  // Loop through input object and populate data arrays
  for (const metric in dataArrays) {
    const dataObj = obj[metric];
    for (const month in dataObj) {
      const monthIndex = labels.indexOf(month);
      if (monthIndex !== -1) {
        dataArrays[metric][monthIndex] = dataObj[month];
      }
    }
  }

  const colors = [
    "#FF6384",
    "#36A2EB",
    "#FFCE56",
    "#4BC0C0",
    "#9966FF",
    "#FF9F40",
  ];

  let colorIndex = 0;

  // Create datasets using data arrays
  for (const metric in dataArrays) {
    if (dataArrays.hasOwnProperty(metric)) {
      const color = colors[colorIndex % colors.length];
      datasets.push({
        label: formatKnownStrings(metric),
        data: dataArrays[metric],
        fill: false,
        borderColor: color,
        pointBorderWidth: 5,
        pointRadius: 10,
        pointHoverRadius: 15,
        tension: 0.4,
        pointHoverBorderWidth: 5,
        yAxisID: "y",
      });
      colorIndex++;
    }
  }

  return { labels, datasets };
}

export const formatKnownStrings = (input: string): string => {
  const formatMap: Record<string, string> = {
    injuryByMonth: "Fall with Injury",
    transferToHospitalByMonth: "Fall Transfered to Hospital",
    fallByMonth: "Total Fall",
  };

  return formatMap[input] || "Unknown Format";
};

const reverseFormatMap: Record<string, string> = {
  "Fall with Injury": "injuryByMonth",
  "Fall Transfered to Hospital": "transferToHospitalByMonth",
  "Total Fall": "fallByMonth",
};

export const reverseFormatKnownStrings = (formatted: string): string => {
  return reverseFormatMap[formatted] || "Unknown Key";
};

const sortLabelsByDate = (labels: any) => {
  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  return [...labels].sort((a: any, b: any) => {
    const [monthA, yearA] = a.trim().replace(/\s+/g, " ").split(" ");
    const [monthB, yearB] = b.trim().replace(/\s+/g, " ").split(" ");

    if (yearA !== yearB) return parseInt(yearA) - parseInt(yearB);

    // If years are same, compare months
    const monthCompare = months.indexOf(monthA) - months.indexOf(monthB);
    return monthCompare;
  });
};

export const doTransformData = (data: any, param: string, keyName: string, totalLabel: string) => {
  
  if (data[param].length) {
    const transformedData = data[param].map((item: any) => ({
      ...item,
    }));

    const totalRow: DataItem = {
      [keyName]: totalLabel,
      percentage: data.totalPercentage || "0",
      count:
        data.totalCount ||
        data.totalRepeatIncident ||
        data.totalIncidentShiftUnit ||
        data.totalIncidents ||
        "0",
    };
    
    transformedData.push(totalRow);
    return transformedData;
  } else return [];
};

export const doTransformDataShift = (
  data: any,
  param: string,
  keyName: string
) => {
  if (!data[param]?.length) {
    return {};
  }

  const groupedData = groupDataByShiftType(data[param]);
  const transformedGroups: Record<string, DataItem[]> = {};

  for (const shiftType in groupedData) {
    let transformedData = groupedData[shiftType].map((item: any) => ({
      ...item,
    }));
    const totalRow = calculateTotalRow(transformedData, keyName);

    transformedData.push(totalRow);
    transformedGroups[shiftType] = transformedData;
  }
  return transformedGroups;
};

const groupDataByShiftType = (data: DataItem[]): Record<string, DataItem[]> => {
  return data?.reduce((groups: Record<string, DataItem[]>, item: DataItem) => {
    const shiftType = item.shiftType;
    if (shiftType && !groups[shiftType]) {
      groups[shiftType] = [];
    }
    shiftType && groups[shiftType].push(item);
    return groups;
  }, {});
};

const calculateTotalRow = (data: any, keyName: string): DataItem => {
  const totalCount = data.reduce(
    (sum: any, item: any) => sum + (item.count || 0),
    0
  );
  const totalPercentage = data.reduce(
    (sum: any, item: any) =>
      sum + (parseFloat(item.percentage?.toString()) || 0),
    0
  );

  return {
    [keyName]: "Total",
    percentage: Number(totalPercentage),
    count: totalCount,
  };
};

interface DataItem {
  [key: string]: any;
  place?: string;
  residentName?: string;
  day?: string;
  count: number;
  shift?: string;
  percentage?: number;
  shiftType?: string;
}

interface BarChartDataset {
  label: string;
  backgroundColor: string | string[];
  borderWidth: number;
  data: number[];
}

// Mockup bar palette (cycled per bar) — keeps charts colorful instead of all-purple.
// Shared "alternating" palette from the Fall Analysis report (MITIGATE_COLORS) so the wound charts
// match Fall's look: blue / teal / red / orange / purple, cycling through progressively lighter shades.
export const BAR_PALETTE = [
  MITIGATE_COLORS.BLUE[0],
  MITIGATE_COLORS.GREEN[0],
  MITIGATE_COLORS.RED[0],
  MITIGATE_COLORS.ORANGE[0],
  MITIGATE_COLORS.PURPLE[0],
  MITIGATE_COLORS.BLUE[1],
  MITIGATE_COLORS.GREEN[1],
  MITIGATE_COLORS.RED[1],
  MITIGATE_COLORS.ORANGE[1],
  MITIGATE_COLORS.PURPLE[1],
  MITIGATE_COLORS.BLUE[2],
  MITIGATE_COLORS.GREEN[2],
  MITIGATE_COLORS.RED[2],
  MITIGATE_COLORS.ORANGE[2],
  MITIGATE_COLORS.PURPLE[2],
  MITIGATE_COLORS.PURPLE[3],
  MITIGATE_COLORS.PURPLE[4],
];

export const barColors = (n: number): string[] =>
  Array.from({ length: n }, (_, i) => BAR_PALETTE[i % BAR_PALETTE.length]);

interface BarChartData {
  labels: string[];
  datasets: BarChartDataset[];
}

export const transformDataForBarChart = (
  data: DataItem[],
  legend: string
): BarChartData => {
  const daysOfWeek = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const labelCountMap: Record<string, number> = {}; // To store counts for each unique label

  // Process each data item
  data.forEach((item) => {
    if (
      !(item.unit && item.unit.includes("Total")) &&
      !(item.area && item.area.includes("Total")) &&
      !(item.residentName && item.residentName.includes("Total")) &&
      !(item.summaryType && item.summaryType.includes("Total")) &&
      !(item.location && item.location.includes("Total")) &&
      !(item.day && item.day.includes("Total")) &&
      !(item.caregiverName && item.caregiverName.includes("Total"))
    ) {
      const label =
        item.location ??
        item.unit ??
        item.area ??
        item.residentName ??
        item.summaryType ??
        item.day ??
        item.caregiverName ??
        "No Data";

      if (labelCountMap[label]) {
        labelCountMap[label] += item.count; // Combine counts for the same label
      } else {
        labelCountMap[label] = item.count; // Initialize the count for a new label
      }
    }
  });

  // Extract labels and counts from the map
  const labels = Object.keys(labelCountMap);
  const counts = Object.values(labelCountMap);

  // Sort labels and counts if days are present
  if (labels.some((label) => daysOfWeek.includes(label))) {
    const sortedLabels = labels
      .filter((label) => daysOfWeek.includes(label))
      .sort((a, b) => {
        return daysOfWeek.indexOf(a) - daysOfWeek.indexOf(b);
      });

    const sortedCounts = sortedLabels.map((label) => labelCountMap[label]);

    // Define a single dataset that includes all counts
    const datasets: BarChartDataset[] = [
      {
        label: legend,
        backgroundColor: barColors(sortedCounts.length),
        borderWidth: 1,
        data: sortedCounts,
      },
    ];

    return { labels: sortedLabels, datasets };
  } else {
    // Define a single dataset that includes all counts
    const datasets: BarChartDataset[] = [
      {
        label: legend,
        backgroundColor: barColors(counts.length),
        borderWidth: 1,
        data: counts,
      },
    ];

    return { labels, datasets };
  }
};

type Incident = {
  FallType: string | null;
  DayOfTheWeek: string;
  IncidentType: string;
  Shift: string;
  PCCRoomDescription: string | null;
  PCCUnitDescription: string | null;
  PCCBedDescription: string | null;
  Activity: string | null;
  Place: string;
  IncidentID: string;
  FacilityTime: string;
  FacilityDate: string;
};

type ResidentInfo = {
  ResidentName: string;
  NumberOfIncident: number;
  Incidents: Incident[];
};

type FacilityInfo = {
  FacilityName: string;
  info: ResidentInfo[];
};

type Data = FacilityInfo[];

export function getTransformedRepeatIncident(data: Data | undefined) {
  if (!data) return [];

  const transformedData = data.map((facility) => {
    const facilityIncidents = facility.info.flatMap((resident) => {
      const { ResidentName, NumberOfIncident, Incidents } = resident;
      return Incidents.map((incident, incidentIndex) => {
        const isFirstRow = incidentIndex === 0;
        return {
          FacilityName: facility.FacilityName,
          ResidentName,
          isFirstRow,
          NumberOfIncident: isFirstRow ? NumberOfIncident : "",
          Location: isFirstRow
            ? `${incident.PCCRoomDescription ?? "-"}/${incident.PCCUnitDescription ?? "-"}/${incident.PCCBedDescription ?? "-"}`
            : "",
          ...incident,
        };
      });
    });

    return {
      FacilityName: facility.FacilityName,
      Incidents: facilityIncidents,
    };
  });

  return transformedData;
}

export const transformForReactTable = (data: any) => {
  const { dataInfo, totalIncidentShiftUnit, totalPercentage } = data;

  // Create a map to organize data by unit
  const unitMap = new Map();

  dataInfo.forEach((item: any) => {
    if (!unitMap.has(item.unit)) {
      unitMap.set(item.unit, {
        unit: item.unit,
        total: { count: 0, percentage: 0 },
      });
    }
    const unitData = unitMap.get(item.unit);

    // Initialize the shift if it doesn't exist
    if (!unitData[item.shift]) {
      unitData[item.shift] = { count: 0, percentage: 0 };
    }

    // Sum up counts and percentages per shift
    unitData[item.shift].count += item.count;
    unitData[item.shift].percentage += item.percentage;

    // Sum up totals
    unitData.total.count += item.count;
    unitData.total.percentage += item.percentage;
  });

  // Convert the map to an array
  const result = Array.from(unitMap.values());

  // Add a total row
  result.push({
    unit: "Total",
    total: {
      count: totalIncidentShiftUnit,
      percentage: totalPercentage,
    },
    ...dataInfo.reduce((acc: any, item: any) => {
      acc[item.shift] = acc[item.shift] || { count: 0, percentage: 0 };
      acc[item.shift].count += item.count;
      acc[item.shift].percentage += item.percentage;
      return acc;
    }, {}),
  });

  return result;
};

type DataInfo = {
  shift: string;
  unit: string;
  count: number;
  percentage: number;
};

type TableData = {
  unit: string;
  [key: string]: any;
};

type ChartData = {
  labels: string[];
  datasets: [
    {
      label: string;
      data: number[];
      backgroundColor: string[];
    },
  ];
};

export const prepareTableData = (
  _dataInfo: DataInfo[],
  totalLabel: string = "Total"
) => {
  const dataInfo = _dataInfo.filter((data) => data.shift !== "Total");

  forEach(dataInfo, (item) => {
    if (item.unit ===  'null' || item.unit === null) {
      item.unit = 'Not listed';
    } 
  });

  // Extract unique shifts, excluding the total label
  const shifts = Array.from(
    new Set(dataInfo.filter((d) => d.shift !== totalLabel).map((d) => d.shift))
  );

  // Default values based on type
  const defaultZero = (type: string) => (type === "count" ? "0" : "0%");

  // Function to create column definitions based on type (count or percentage)
  const createColumns = (type: "count" | "percentage") => [
    {
      Header: "Unit",
      accessor: "unit",
      Cell: ({ value }: any) => (value),
    },
    ...shifts.map((shift) => ({
      Header: shift,
      accessor: `${shift}_${type}`,
      Cell: ({ value }: any) =>
        type === "percentage" && typeof value === "number"
          ? `${value}%`
          : value || defaultZero(type),
    })),
    {
      Header: totalLabel,
      accessor: `total_${type}`,
      Cell: ({ value }: any) =>
        type === "percentage" && typeof value === "number"
          ? `${value}%`
          : value || defaultZero(type),
    },
  ];

  const countColumns = createColumns("count");
  const percentageColumns = createColumns("percentage");

  // Map to store unit data for counts and percentages
  const unitMap: Record<string, TableData> = {};

  // Initialize data for each unit
  dataInfo.forEach(({ unit, shift, count, percentage }) => {
    if (!unitMap[unit]) {
      unitMap[unit] = { unit, total_count: 0, total_percentage: 0 };
    }
    unitMap[unit][`${shift}_count`] = count;
    unitMap[unit][`${shift}_percentage`] = percentage;
    unitMap[unit]["total_count"] += count;
    unitMap[unit]["total_percentage"] += percentage;
  });

  // Calculate total for each column
  const totalData: TableData = {
    unit: totalLabel,
    total_count: 0,
    total_percentage: 0,
  };
  shifts.forEach((shift) => {
    totalData[`${shift}_count`] = 0;
    totalData[`${shift}_percentage`] = 0;
  });

  Object.values(unitMap).forEach((unitData) => {
    shifts.forEach((shift) => {
      totalData[`${shift}_count`] += unitData[`${shift}_count`] || 0;
      totalData[`${shift}_percentage`] += unitData[`${shift}_percentage`] || 0;
    });
    totalData["total_count"] += unitData["total_count"];
    totalData["total_percentage"] += Number(unitData["total_percentage"]);
  });

  // Default total percentage to 100
  totalData["total_percentage"] = `100%`;

  shifts.forEach((shift) => {
    const shiftPercentageKey = `${shift}_percentage`;
    const totalShiftPercentage = Number(totalData[shiftPercentageKey]);
    totalData[shiftPercentageKey] = totalShiftPercentage + "%";
  });

  const data = Object.values(unitMap);
  data.push(totalData);
  return {
    countColumns,
    percentageColumns,
    data,
  };
};

export const prepareChartData = (
  dataInfo: DataInfo[]
): { shiftChartData: ChartData; unitChartData: ChartData } => {
  // Extract unique shifts and units
  const shifts = Array.from(
    new Set(
      dataInfo.filter((item) => item.shift !== "Total").map((d) => d.shift)
    )
  );
  const units = Array.from(
    new Set(
      dataInfo.filter((item) => item.unit !== undefined).map((d) => d.unit)
    )
  );

  // Prepare data for Shift to Count Chart
  const shiftCounts: Record<string, number> = {};
  shifts.forEach((shift) => (shiftCounts[shift] = 0)); // Initialize counts
  dataInfo.forEach((d) => {
    shiftCounts[d.shift] += d.count;
  });

  const shiftChartData: ChartData = {
    labels: shifts,
    datasets: [
      {
        label: "Count by Shift",
        data: shifts.map((shift) => shiftCounts[shift]),
        backgroundColor: ["#b580ff"],
      },
    ],
  };

  // Prepare data for Unit to Count Chart
  const unitCounts: Record<string, number> = {};
  units.forEach((unit) => (unitCounts[unit] = 0)); // Initialize counts
  dataInfo.forEach((d) => {
    unitCounts[d.unit] += d.count;
  });

  const unitChartData: ChartData = {
    labels: units,
    datasets: [
      {
        label: "Count by Unit",
        data: units.map((unit) => unitCounts[unit]),
        backgroundColor: ["#b580ff"],
      },
    ],
  };

  return { shiftChartData, unitChartData };
};

export const triggerChartExport = async (
  chartID: string,
  title: string = "chart-export",
  subtitle: string = ""
) => {
  const pdf = new jsPDF("landscape", "pt", "a4");
  const pdfWidth = pdf.internal.pageSize.getWidth();
  const pdfHeight = pdf.internal.pageSize.getHeight();

  const chartElement = document.getElementById(chartID);

  if (!chartElement) {
    console.error(`Element with ID "${chartID}" not found.`);
    return;
  }

  const canvas = await html2canvas(chartElement, { scale: 2 });
  const imgData = canvas.toDataURL("image/jpeg", 1.0);
  const imgProps = pdf.getImageProperties(imgData);

  let imgWidth = pdfWidth;
  let imgHeight = (imgProps.height * pdfWidth) / imgProps.width;

  if (imgHeight > pdfHeight - 100) {
    imgHeight = pdfHeight - 100;
    imgWidth = (imgProps.width * imgHeight) / imgProps.height;
  }

  pdf.setTextColor("#8b47ea");

  pdf.setFontSize(18);
  pdf.text(title, 20, 40);

  pdf.setFontSize(12);
  pdf.text(subtitle, 20, 60);

  pdf.addImage(imgData, "JPEG", 10, 70, imgWidth, imgHeight);

  pdf.save(`${title}.pdf`);
};

export const triggerMultiExport = async (
  subComponentIDs: any[],
  title: string = "./pdf/generate.pdf",
  subtitle: string = ""
) => {
  const pdf = new jsPDF("landscape", "pt", "a4");
  const pdfWidth = pdf.internal.pageSize.getWidth();
  const pdfHeight = pdf.internal.pageSize.getHeight();

  for (let i = 0; i < subComponentIDs.length; i++) {
    const id = subComponentIDs[i];
    const subComponentElement = document.getElementById(id);

    if (subComponentElement) {
      // Capture the element as a canvas
      const canvas = await html2canvas(subComponentElement, { scale: 2 });
      const imgData = canvas.toDataURL("image/jpeg", 1.0);
      const imgProps = pdf.getImageProperties(imgData);
      let imgWidth = pdfWidth;
      let imgHeight = (imgProps.height * pdfWidth) / imgProps.width;

      // Adjust image height if it exceeds the page height
      if (imgHeight > pdfHeight - 50) {
        imgHeight = pdfHeight - 50;
        imgWidth = (imgProps.width * imgHeight) / imgProps.height;
      }

      // Add a new page for each subcomponent except the first one
      if (i > 0) {
        pdf.addPage();
      }

      // Set Colour
      pdf.setTextColor("#8b47ea");

      // Add title to the page
      pdf.setFontSize(18);
      pdf.text(title, 20, 40);

      // Add subtitle to the page
      pdf.setFontSize(12);
      pdf.text(subtitle, 20, 60);

      // Add the image to the PDF
      pdf.addImage(imgData, "JPEG", 0, 70, imgWidth, imgHeight);
    }
  }

  // Save the PDF
  pdf.save(`${title}.pdf`);
};

export const fetchIncidentData = async (
  clickedIncident: any,
  refetchSelectedIncident: any,
  incidentTypesObject: any,
  createForm: any,
  updateForm: any,
  navigate: any
) => {
  if (clickedIncident?.incidentId?.length > 0) {
    try {
      const response = await refetchSelectedIncident();
      const selectedIncident = response?.data?.data?.data;
      const incidentType = incidentTypesObject.find(
        (incident: any) => incident.flag === selectedIncident.RecordTypeId
      );
      viewClickedIncident(
        createForm,
        updateForm,
        selectedIncident,
        incidentType,
        navigate
      );
      return selectedIncident;
    } catch (err) {
      console.log({ err });
      return null;
    }
  }
  return null;
};
